import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './SundeepInvoiceProcess.css';

// Toast component
const Toast = ({ message, type, onClose }) => (
  <div className={`toast ${type}`}> 
    {message}
    <button className="toast-close" onClick={onClose}>×</button>
  </div>
);

const SundeepInvoiceProcess = () => {
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [skuMatches, setSkuMatches] = useState({});
  const [priceMatches, setPriceMatches] = useState({});
  const [allItemsMatched, setAllItemsMatched] = useState(false);
  const [allPricesMatched, setAllPricesMatched] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  
  // OAuth authentication state
  const [authStatus, setAuthStatus] = useState({
    authenticated: false,
    needsReconnect: false,
    message: ''
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Load invoice files and check auth status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check for auth success in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('auth') === 'success') {
      setToast({ message: 'Google Drive connected successfully!', type: 'success' });
      // Remove the auth parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Recheck auth status and load files
      checkAuthStatus();
    }
  }, []);

  // Toast auto-hide effect
  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ message: '', type: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const checkAuthStatus = async () => {
    try {
      setIsCheckingAuth(true);
      const response = await apiCall('/api/sundeep-invoices/oauth/status', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthStatus(data);
        
        // If authenticated, load invoice files
        if (data.authenticated) {
          loadInvoiceFiles();
        }
      } else {
        setAuthStatus({
          authenticated: false,
          needsReconnect: true,
          message: 'Failed to check authentication status'
        });
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setAuthStatus({
        authenticated: false,
        needsReconnect: true,
        message: 'Error checking authentication status'
      });
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const initiateOAuth = async () => {
    try {
      setIsLoading(true);
      const response = await apiCall('/api/sundeep-invoices/oauth/initiate', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        const errorData = await response.json();
        setToast({ message: errorData.error || 'Failed to initiate OAuth', type: 'error' });
      }
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      setToast({ message: 'Error initiating OAuth flow', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadInvoiceFiles = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiCall('/api/sundeep-invoices/files', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoiceFiles(data.files || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load invoice files');
      }
    } catch (error) {
      console.error('Error loading invoice files:', error);
      setError('Error loading invoice files. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const extractInvoiceNumber = (filename) => {
    // Extract invoice number from filename like "Inv_98409_from_Sun_Deep_Inc._380.pdf"
    const match = filename.match(/Inv_(\d+)_from_Sun_Deep_Inc\._\d+\.pdf/);
    return match ? match[1] : null;
  };

  const checkSkuMatch = async (itemCode, description) => {
    try {
      const response = await apiCall('/api/sundeep-invoices/check-sku-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item_code: itemCode,
          description: description
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Error checking SKU match');
        return { matched: false, message: 'Error checking match' };
      }
    } catch (error) {
      console.error('Error checking SKU match:', error);
      return { matched: false, message: 'Error checking match' };
    }
  };

  const checkPriceMatch = async (igdSku, invoicePrice) => {
    try {
      const response = await apiCall('/api/sundeep-invoices/check-price-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          igd_sku: igdSku,
          invoice_price: invoicePrice
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Error checking price match');
        return { matched: false, message: 'Error checking price match' };
      }
    } catch (error) {
      console.error('Error checking price match:', error);
      return { matched: false, message: 'Error checking price match' };
    }
  };

  const handleDownload = async (file) => {
    try {
      setIsLoading(true);
      setError(null);
      setCurrentFile(file);
      
      const response = await apiCall('/api/sundeep-invoices/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId: file.id,
          filename: file.name
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const parsedData = data.parsed_data;
        setParsedData(parsedData);
        
        // Check SKU matches and price matches for all line items
        const skuMatches = {};
        const priceMatches = {};
        let allItemsMatched = true;
        let allPricesMatched = true;
        
        for (let i = 0; i < parsedData.line_items.length; i++) {
          const item = parsedData.line_items[i];
          
          // Check SKU match first
          const skuMatchResult = await checkSkuMatch(item.item_code, item.description);
          skuMatches[i] = skuMatchResult;
          
          if (!skuMatchResult.matched) {
            allItemsMatched = false;
            allPricesMatched = false;
            // If no SKU match, price match is automatically failed
            priceMatches[i] = { matched: false, message: 'Product not found' };
          } else {
            // Check price match if SKU was found
            const priceMatchResult = await checkPriceMatch(skuMatchResult.igd_sku, item.price_each);
            priceMatches[i] = priceMatchResult;
            
            if (!priceMatchResult.matched) {
              allPricesMatched = false;
              // If price doesn't match, mark SKU as failed too (for fuzzy matches)
              if (skuMatchResult.match_type === 'fuzzy') {
                skuMatches[i] = { 
                  ...skuMatchResult, 
                  matched: false, 
                  message: 'Close match but price mismatch suggests wrong product' 
                };
                allItemsMatched = false;
              }
            }
          }
        }
        
        setSkuMatches(skuMatches);
        setPriceMatches(priceMatches);
        setAllItemsMatched(allItemsMatched);
        setAllPricesMatched(allPricesMatched);
        setShowApprovalDialog(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to download and parse invoice');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setError('Error downloading invoice. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      // Prepare all line items for bulk save
      const lineItems = [];
      for (let i = 0; i < parsedData.line_items.length; i++) {
        const item = parsedData.line_items[i];
        const skuMatch = skuMatches[i];
        const priceMatch = priceMatches[i];
        
        if (skuMatch && skuMatch.matched && priceMatch && priceMatch.matched) {
          // Clean numeric fields by removing commas
          const cleanNumeric = (value) => {
            if (typeof value === 'string') {
              return value.replace(/,/g, '');
            }
            return value;
          };
          
          // Prepare payload for API
          const payload = {
            date: parsedData.invoice_date, // should be YYYY-MM-DD
            invoice_number: parsedData.invoice_number,
            item_code: item.item_code,
            igd_sku_number: skuMatch.igd_sku, // from match
            quantity: cleanNumeric(item.quantity),
            price_each: cleanNumeric(item.price_each),
            amount: cleanNumeric(item.amount),
            filename: currentFile.name
          };
          lineItems.push(payload);
        }
      }
      
      // Use bulk save endpoint for atomic operation
      const response = await apiCall('/api/sundeep_invoice_log/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line_items: lineItems
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setToast({ message: data.message || 'All invoice line items saved successfully!', type: 'success' });
        setShowApprovalDialog(false);
        setParsedData(null);
        setCurrentFile(null);
        loadInvoiceFiles();
      } else {
        const errorData = await response.json();
        setToast({ message: errorData.error || 'Failed to save invoice line items', type: 'error' });
      }
    } catch (error) {
      console.error('Error saving invoice log:', error);
      setToast({ message: 'Error saving invoice log. Please check the console for details.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowApprovalDialog(false);
    setParsedData(null);
    setCurrentFile(null);
    setSkuMatches({});
    setPriceMatches({});
    setAllItemsMatched(false);
    setAllPricesMatched(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    
    // Convert string amounts with commas to numbers
    let numericAmount = amount;
    if (typeof amount === 'string') {
      // Remove commas and convert to number
      numericAmount = parseFloat(amount.replace(/,/g, ''));
    }
    
    // Check if the conversion was successful
    if (isNaN(numericAmount)) {
      return '$0.00';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numericAmount);
  };

  return (
    <div className="sundeep-invoice-process">
      {/* Toast notification */}
      {toast.message && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />
      )}
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Sundeep Invoice Process</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Connect to Google Drive to access Sundeep invoice files</li>
              <li>Review available invoice files and their processing status</li>
              <li>Download and parse PDF invoices for data extraction</li>
              <li>Approve parsed data to save to the invoice log</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Authentication Status Section */}
        {isCheckingAuth ? (
          <div className="section">
            <div className="loading">Checking Google Drive connection...</div>
          </div>
        ) : !authStatus.authenticated ? (
          <div className="section">
            <div className="auth-status">
              <div className="auth-icon">🔐</div>
              <h2>Google Drive Connection Required</h2>
              <p className="auth-message">{authStatus.message}</p>
              <button
                className="reconnect-button"
                onClick={initiateOAuth}
                disabled={isLoading}
              >
                {isLoading ? 'Connecting...' : '🔗 Reconnect Google Drive'}
              </button>
              <div className="auth-help">
                <p>This will open Google's authorization page where you can:</p>
                <ul>
                  <li>Sign in to your Google account</li>
                  <li>Grant access to your Google Drive</li>
                  <li>Allow the app to read Sundeep invoice files</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          /* Invoice Files Section - Only show when authenticated */
          <div className="section">
            <div className="section-header">
              <h2>📄 Available Invoice Files</h2>
              <div className="auth-indicator">
                <span className="auth-status-badge connected">✅ Connected to Google Drive</span>
              </div>
            </div>
            {isLoading ? (
              <div className="loading">Loading invoice files...</div>
            ) : error ? (
              <div className="error">❌ {error}</div>
            ) : successMessage ? (
              <div className="success">✅ {successMessage}</div>
            ) : invoiceFiles.length > 0 ? (
              <div className="invoice-files-list">
                <h3>Found {invoiceFiles.length} invoice files:</h3>
                {invoiceFiles.map((file, index) => {
                  const invoiceNumber = extractInvoiceNumber(file.name);
                  const isCompleted = file.status === 'completed';
                  
                  return (
                    <div 
                      key={file.id} 
                      className={`invoice-file-item ${isCompleted ? 'completed' : ''}`}
                    >
                      <div className="file-info">
                        <div className="file-name">{file.name}</div>
                        <div className="file-details">
                          {invoiceNumber && <span>🔢 Invoice: {invoiceNumber}</span>}
                        </div>
                      </div>
                      <div className="file-actions">
                        {isCompleted ? (
                          <div className="status-completed">COMPLETED</div>
                        ) : (
                          <button
                            className="download-button"
                            onClick={() => handleDownload(file)}
                            disabled={isLoading}
                          >
                            {isLoading && currentFile?.id === file.id ? 'Processing...' : 'DOWNLOAD'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-files">No invoice files found in Google Drive.</div>
            )}
          </div>
        )}

        {/* Approval Dialog */}
        {showApprovalDialog && parsedData && (
          <div className="approval-dialog-overlay">
            <div className="approval-dialog">
              <div className="dialog-header">
                <h3>Review Parsed Invoice Data</h3>
                <button className="close-button" onClick={handleCancel}>×</button>
              </div>
              
              <div className="dialog-content">
                <div className="invoice-summary">
                  <h4>Invoice Summary</h4>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Invoice Number:</label>
                      <span>{parsedData.invoice_number || 'N/A'}</span>
                    </div>
                    <div className="summary-item">
                      <label>Invoice Date:</label>
                      <span>{parsedData.invoice_date || 'N/A'}</span>
                    </div>
                    <div className="summary-item">
                      <label>Total Amount:</label>
                      <span>{formatCurrency(parsedData.total)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Line Items:</label>
                      <span>{parsedData.line_items?.length || 0}</span>
                    </div>
                  </div>
                </div>

                {parsedData.line_items && parsedData.line_items.length > 0 && (
                  <div className="line-items-section">
                    <h4>Line Items</h4>
                    <div className="line-items-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Description</th>
                            <th>Quantity</th>
                            <th>Price Each</th>
                            <th>Amount</th>
                            <th>Product Match</th>
                            <th>Price Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedData.line_items.map((item, index) => {
                            const skuMatch = skuMatches[index];
                            const priceMatch = priceMatches[index];
                            return (
                              <tr key={index}>
                                <td>{item.item_code || 'N/A'}</td>
                                <td>{item.description || 'N/A'}</td>
                                <td>{item.quantity || 'N/A'}</td>
                                <td>{formatCurrency(item.price_each)}</td>
                                <td>{formatCurrency(item.amount)}</td>
                                <td>
                                  {skuMatch ? (
                                    <div className={`sku-match ${skuMatch.matched ? 'matched' : 'not-matched'}`}>
                                      <span className={`match-icon ${skuMatch.matched ? 'success' : 'error'}`}>
                                        {skuMatch.matched ? '✓' : '✗'}
                                      </span>
                                      <div className="match-details">
                                        <div className="match-text">{skuMatch.message}</div>
                                        {skuMatch.confidence && skuMatch.confidence < 100 && (
                                          <div className="confidence-score">
                                            Confidence: {skuMatch.confidence}%
                                          </div>
                                        )}
                                        {skuMatch.match_type === 'fuzzy' && skuMatch.matched && (
                                          <div className="fuzzy-details">
                                            <small>Item Code: {skuMatch.item_code_score}% | Description: {skuMatch.desc_score}%</small>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <span>Checking...</span>
                                  )}
                                </td>
                                <td>
                                  {priceMatch ? (
                                    <div className={`price-match ${priceMatch.matched ? 'matched' : 'not-matched'}`}>
                                      <span className={`match-icon ${priceMatch.matched ? 'success' : 'error'}`}>
                                        {priceMatch.matched ? '✓' : '✗'}
                                      </span>
                                      <span className="match-text">{priceMatch.message}</span>
                                    </div>
                                  ) : (
                                    <span>Checking...</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="dialog-actions">
                  <button
                    className="cancel-button"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className={`approve-button ${allItemsMatched && allPricesMatched ? 'enabled' : ''}`}
                    onClick={handleApprove}
                    disabled={isLoading || !allItemsMatched || !allPricesMatched}
                  >
                    {isLoading ? 'Processing...' : 'Approve & Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SundeepInvoiceProcess; 